import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import {
  Receipt, FileInput, ClipboardList, AlertTriangle, Clock,
  CircleDollarSign, CheckCircle2, PauseCircle, Activity,
} from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

const formatCurrency = (n: number | null | undefined) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })
    .format(Number(n || 0));

const formatDate = (d: string | null | undefined) =>
  d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

function KpiCard({
  title, value, icon: Icon, loading, borderColor = "border-slate-400", hint,
}: {
  title: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
  loading?: boolean;
  borderColor?: string;
  hint?: string;
}) {
  return (
    <div className={`border-l-[3px] ${borderColor} bg-card rounded-r-lg shadow-sm`}>
      <div className="p-3 sm:p-6">
        <div className="space-y-0.5 sm:space-y-1">
          <p className="text-xs sm:text-sm font-medium text-muted-foreground flex items-center gap-1.5">
            <Icon className="h-3.5 w-3.5" />
            {title}
          </p>
          <div className="text-lg sm:text-2xl font-semibold truncate">
            {loading ? <Skeleton className="h-7 w-24" /> : value}
          </div>
          {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
        </div>
      </div>
    </div>
  );
}

function ChartCard({
  title, description, icon: Icon, children,
}: {
  title: string;
  description?: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <div className="border-l-[3px] border-slate-400 bg-card rounded-r-lg shadow-sm">
      <div className="px-4 py-3">
        <h2 className="text-base font-semibold flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
          {title}
        </h2>
        {description && <p className="text-sm text-muted-foreground mt-0.5">{description}</p>}
      </div>
      <div className="px-4 pb-4">{children}</div>
    </div>
  );
}

function EmptyState({ message, icon: Icon }: { message: string; icon: React.ComponentType<{ className?: string }> }) {
  return (
    <div className="h-[180px] flex items-center justify-center text-muted-foreground">
      <div className="text-center">
        <Icon className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">{message}</p>
      </div>
    </div>
  );
}

function AgingBuckets({ data }: { data: Array<{ bucket: string; count: number; amount: number }> }) {
  const max = Math.max(1, ...data.map((d) => d.amount));
  return (
    <div className="space-y-2">
      {data.map((b) => (
        <div key={b.bucket} className="space-y-1">
          <div className="flex justify-between text-sm">
            <span className="font-medium">{b.bucket}</span>
            <span className="text-muted-foreground">{b.count} · {formatCurrency(b.amount)}</span>
          </div>
          <div className="h-2 bg-muted rounded overflow-hidden">
            <div
              className={`h-full ${
                b.bucket === "Current" ? "bg-emerald-500" :
                b.bucket === "90+" ? "bg-red-500" : "bg-amber-500"
              }`}
              style={{ width: `${(b.amount / max) * 100}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

export function ReceivablesTab() {
  const { sessionId } = useAuth();

  const kpis = useQuery<{ openCount: number; openBalance: number; overdueAmount: number; avgDaysToPay: number }>({
    queryKey: ["/api/finance/ar/kpis"],
    queryFn: () => api.get(`/finance/ar/kpis`, sessionId),
  });
  const aging = useQuery<Array<{ bucket: string; count: number; amount: number }>>({
    queryKey: ["/api/finance/ar/aging"],
    queryFn: () => api.get(`/finance/ar/aging`, sessionId),
  });
  const topCustomers = useQuery<Array<{ accountId: string; accountName: string; balance: number; count: number }>>({
    queryKey: ["/api/finance/ar/top-customers"],
    queryFn: () => api.get(`/finance/ar/top-customers`, sessionId),
  });
  const upcoming = useQuery<Array<{ id: string; invoiceNumber: string; accountName: string; dueDate: string | null; balance: number }>>({
    queryKey: ["/api/finance/ar/upcoming"],
    queryFn: () => api.get(`/finance/ar/upcoming`, sessionId),
  });

  return (
    <div className="space-y-6">
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <KpiCard title="Open Invoices" value={kpis.data?.openCount ?? 0} icon={Receipt} loading={kpis.isLoading} />
        <KpiCard title="Open AR Balance" value={formatCurrency(kpis.data?.openBalance)} icon={CircleDollarSign}
          loading={kpis.isLoading} borderColor="border-blue-500" />
        <KpiCard title="Overdue" value={formatCurrency(kpis.data?.overdueAmount)} icon={AlertTriangle}
          loading={kpis.isLoading} borderColor="border-red-500" />
        <KpiCard title="Avg Days to Pay" value={`${kpis.data?.avgDaysToPay ?? 0}d`} icon={Clock}
          loading={kpis.isLoading} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <ChartCard title="AR Aging" description="Open balance by days past due" icon={AlertTriangle}>
          {aging.isLoading ? <Skeleton className="h-[180px] w-full" />
            : (aging.data || []).every((b) => b.amount === 0) ? <EmptyState message="No open receivables" icon={Receipt} />
            : <AgingBuckets data={aging.data || []} />}
        </ChartCard>

        <ChartCard title="Top Customers" description="By open balance" icon={CircleDollarSign}>
          {topCustomers.isLoading ? <Skeleton className="h-[180px] w-full" />
            : !topCustomers.data?.length ? <EmptyState message="No open balances" icon={CircleDollarSign} />
            : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer</TableHead>
                    <TableHead className="text-right">Invoices</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topCustomers.data.map((c) => (
                    <TableRow key={c.accountId}>
                      <TableCell>
                        <Link href={`/accounts/${c.accountId}`} className="font-medium hover:underline">
                          {c.accountName}
                        </Link>
                      </TableCell>
                      <TableCell className="text-right">{c.count}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(c.balance)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
        </ChartCard>
      </div>

      <ChartCard title="Due in Next 30 Days" icon={Clock}>
        {upcoming.isLoading ? <Skeleton className="h-[180px] w-full" />
          : !upcoming.data?.length ? <EmptyState message="Nothing due soon" icon={Clock} />
          : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Due</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {upcoming.data.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell>
                      <Link href={`/invoices/${u.id}`} className="font-medium font-mono text-xs hover:underline">
                        {u.invoiceNumber}
                      </Link>
                    </TableCell>
                    <TableCell>{u.accountName}</TableCell>
                    <TableCell className="text-muted-foreground text-xs">{formatDate(u.dueDate)}</TableCell>
                    <TableCell className="text-right font-medium">{formatCurrency(u.balance)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
      </ChartCard>
    </div>
  );
}

export function PayablesTab() {
  const { sessionId } = useAuth();
  const kpis = useQuery<{ openCount: number; openBalance: number; overdueAmount: number; onHoldAmount: number; exceptionCount: number }>({
    queryKey: ["/api/finance/ap/kpis"],
    queryFn: () => api.get(`/finance/ap/kpis`, sessionId),
  });
  const aging = useQuery<Array<{ bucket: string; count: number; amount: number }>>({
    queryKey: ["/api/finance/ap/aging"],
    queryFn: () => api.get(`/finance/ap/aging`, sessionId),
  });
  const topVendors = useQuery<Array<{ vendorId: string; vendorName: string; balance: number; count: number }>>({
    queryKey: ["/api/finance/ap/top-vendors"],
    queryFn: () => api.get(`/finance/ap/top-vendors`, sessionId),
  });

  return (
    <div className="space-y-6">
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-5">
        <KpiCard title="Open Bills" value={kpis.data?.openCount ?? 0} icon={FileInput} loading={kpis.isLoading} />
        <KpiCard title="Open AP Balance" value={formatCurrency(kpis.data?.openBalance)} icon={CircleDollarSign}
          loading={kpis.isLoading} borderColor="border-blue-500" />
        <KpiCard title="Overdue" value={formatCurrency(kpis.data?.overdueAmount)} icon={AlertTriangle}
          loading={kpis.isLoading} borderColor="border-red-500" />
        <KpiCard title="On Hold" value={formatCurrency(kpis.data?.onHoldAmount)} icon={PauseCircle}
          loading={kpis.isLoading} borderColor="border-amber-500" />
        <KpiCard title="Exceptions" value={kpis.data?.exceptionCount ?? 0} icon={Activity}
          loading={kpis.isLoading} borderColor="border-red-500" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <ChartCard title="AP Aging" description="Open balance by days past due" icon={AlertTriangle}>
          {aging.isLoading ? <Skeleton className="h-[180px] w-full" />
            : (aging.data || []).every((b) => b.amount === 0) ? <EmptyState message="No open payables" icon={FileInput} />
            : <AgingBuckets data={aging.data || []} />}
        </ChartCard>

        <ChartCard title="Top Vendors" description="By open balance" icon={CircleDollarSign}>
          {topVendors.isLoading ? <Skeleton className="h-[180px] w-full" />
            : !topVendors.data?.length ? <EmptyState message="No open balances" icon={CircleDollarSign} />
            : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Vendor</TableHead>
                    <TableHead className="text-right">Bills</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topVendors.data.map((v) => (
                    <TableRow key={v.vendorId}>
                      <TableCell>
                        <Link href={`/vendors/${v.vendorId}`} className="font-medium hover:underline">
                          {v.vendorName}
                        </Link>
                      </TableCell>
                      <TableCell className="text-right">{v.count}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(v.balance)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
        </ChartCard>
      </div>
    </div>
  );
}

export function PurchasingTab() {
  const { sessionId } = useAuth();
  const kpis = useQuery<{ openCount: number; committed: number; expectedThisWeek: number; lateAmount: number }>({
    queryKey: ["/api/finance/po/kpis"],
    queryFn: () => api.get(`/finance/po/kpis`, sessionId),
  });
  const commitments = useQuery<{ committed: number; received: number; invoiced: number; paid: number }>({
    queryKey: ["/api/finance/po/commitments"],
    queryFn: () => api.get(`/finance/po/commitments`, sessionId),
  });
  const onTime = useQuery<{ onTime: number; late: number; rate: number }>({
    queryKey: ["/api/finance/po/on-time-rate"],
    queryFn: () => api.get(`/finance/po/on-time-rate`, sessionId),
  });
  const topVendors = useQuery<Array<{ vendorId: string; vendorName: string; committed: number; count: number }>>({
    queryKey: ["/api/finance/po/top-vendors"],
    queryFn: () => api.get(`/finance/po/top-vendors`, sessionId),
  });
  const upcoming = useQuery<Array<{ id: string; poNumber: string; vendorName: string; targetDate: string | null; total: number }>>({
    queryKey: ["/api/finance/po/upcoming-receipts"],
    queryFn: () => api.get(`/finance/po/upcoming-receipts`, sessionId),
  });

  return (
    <div className="space-y-6">
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <KpiCard title="Open POs" value={kpis.data?.openCount ?? 0} icon={ClipboardList} loading={kpis.isLoading} />
        <KpiCard title="Open Commitment" value={formatCurrency(kpis.data?.committed)} icon={CircleDollarSign}
          loading={kpis.isLoading} borderColor="border-blue-500" />
        <KpiCard title="Expected This Week" value={formatCurrency(kpis.data?.expectedThisWeek)} icon={Clock}
          loading={kpis.isLoading} borderColor="border-green-500" />
        <KpiCard title="Late" value={formatCurrency(kpis.data?.lateAmount)} icon={AlertTriangle}
          loading={kpis.isLoading} borderColor="border-red-500" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <ChartCard title="Commitments Waterfall" description="Committed → Received → Invoiced → Paid" icon={ClipboardList}>
          {commitments.isLoading ? <Skeleton className="h-[180px] w-full" />
            : !commitments.data ? <EmptyState message="No commitments" icon={ClipboardList} />
            : (
              <div className="space-y-3">
                {[
                  { stage: "Committed", amount: commitments.data.committed, color: "bg-blue-500" },
                  { stage: "Received", amount: commitments.data.received, color: "bg-emerald-500" },
                  { stage: "Invoiced", amount: commitments.data.invoiced, color: "bg-amber-500" },
                  { stage: "Paid", amount: commitments.data.paid, color: "bg-purple-500" },
                ].map((s) => {
                  const max = Math.max(commitments.data!.committed, 1);
                  return (
                    <div key={s.stage} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="font-medium">{s.stage}</span>
                        <span className="text-muted-foreground">{formatCurrency(s.amount)}</span>
                      </div>
                      <div className="h-2 bg-muted rounded overflow-hidden">
                        <div className={`h-full ${s.color}`} style={{ width: `${Math.min(100, (s.amount / max) * 100)}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
        </ChartCard>

        <ChartCard title="On-Time Delivery" description="Received POs vs expected date" icon={CheckCircle2}>
          {onTime.isLoading ? <Skeleton className="h-[180px] w-full" />
            : !onTime.data || (onTime.data.onTime === 0 && onTime.data.late === 0)
              ? <EmptyState message="No completed POs" icon={CheckCircle2} />
              : (
                <div className="flex flex-col items-center justify-center h-[180px] gap-3">
                  <div className="text-5xl font-semibold">{onTime.data.rate}%</div>
                  <div className="text-sm text-muted-foreground">on-time delivery rate</div>
                  <div className="flex gap-3 text-sm">
                    <Badge variant="outline">On time: {onTime.data.onTime}</Badge>
                    <Badge variant="outline">Late: {onTime.data.late}</Badge>
                  </div>
                </div>
              )}
        </ChartCard>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <ChartCard title="Top Vendors by Open Commitment" icon={CircleDollarSign}>
          {topVendors.isLoading ? <Skeleton className="h-[180px] w-full" />
            : !topVendors.data?.length ? <EmptyState message="No open POs" icon={ClipboardList} />
            : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Vendor</TableHead>
                    <TableHead className="text-right">Open POs</TableHead>
                    <TableHead className="text-right">Committed</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topVendors.data.map((v) => (
                    <TableRow key={v.vendorId}>
                      <TableCell>
                        <Link href={`/vendors/${v.vendorId}`} className="font-medium hover:underline">
                          {v.vendorName}
                        </Link>
                      </TableCell>
                      <TableCell className="text-right">{v.count}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(v.committed)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
        </ChartCard>

        <ChartCard title="Upcoming Receipts (30 days)" icon={Clock}>
          {upcoming.isLoading ? <Skeleton className="h-[180px] w-full" />
            : !upcoming.data?.length ? <EmptyState message="No expected receipts" icon={Clock} />
            : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>PO</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Expected</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {upcoming.data.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell>
                        <Link href={`/purchase-orders/${u.id}`} className="font-medium font-mono text-xs hover:underline">
                          {u.poNumber}
                        </Link>
                      </TableCell>
                      <TableCell>{u.vendorName}</TableCell>
                      <TableCell className="text-muted-foreground text-xs">{formatDate(u.targetDate)}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(u.total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
        </ChartCard>
      </div>
    </div>
  );
}

export default function FinancePage() {
  const [tab, setTab] = useState("ar");
  return (
    <div className="space-y-6">
      <PageHeader
        title="Finance"
        description="Receivables, payables, and open purchasing commitments"
      />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="ar">
            <Receipt className="h-4 w-4 mr-1.5" /> Receivables
          </TabsTrigger>
          <TabsTrigger value="ap">
            <FileInput className="h-4 w-4 mr-1.5" /> Payables
          </TabsTrigger>
          <TabsTrigger value="po">
            <ClipboardList className="h-4 w-4 mr-1.5" /> Purchasing
          </TabsTrigger>
        </TabsList>
        <TabsContent value="ar" className="mt-6"><ReceivablesTab /></TabsContent>
        <TabsContent value="ap" className="mt-6"><PayablesTab /></TabsContent>
        <TabsContent value="po" className="mt-6"><PurchasingTab /></TabsContent>
      </Tabs>
    </div>
  );
}
